/**
 * Expanded shipment view for the Internal Tracking console.
 *
 * Left  : the vendor's real timeline (Atlantic/ICL) — empty for manual vendors.
 * Right : our customer-facing timeline (what the customer sees).
 * Below : shipment details, boxes (editable weight, per-box or shared update)
 *         and a manual "publish update" form that needs no suggestion.
 */
import { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import { Icon } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import { useNotification } from "context/notification.context";
import {
  getInternalDetail,
  manualInternalUpdate,
  setInternalBoxWeight,
  setInternalFields,
  getTrackingStages,
} from "network/service";

const STATUS_OPTIONS = ["Pending", "Picked Up", "Intransit", "On Hold", "Delivered", "Issue"];
const STATUS_BY_CODE = { 0: "Pending", 1: "Intransit", 2: "On Hold", 3: "Delivered", 4: "Issue", 5: "Picked Up" };

// Saved phrases may contain {destHub}/{eta} placeholders. The engine fills them
// in; when staff pick a phrase by hand we must fill them too, otherwise the raw
// "{destHub}" text would be published to the customer.
const DEST_HUB = {
  UK: "UK", "UNITED KINGDOM": "UK", GERMANY: "GERMANY FRANKFURT", AUSTRALIA: "AUSTRALIA",
  USA: "USA", "UNITED STATES": "USA", CANADA: "CANADA", IRELAND: "IRELAND",
  "NEW ZEALAND": "NEW ZEALAND", OMAN: "OMAN", UAE: "DUBAI", "UNITED ARAB EMIRATES": "DUBAI",
};
const renderPhrase = (tpl, detail) => {
  const country = String(detail?.country || "").toUpperCase().trim();
  const hub = DEST_HUB[country] || country || "DESTINATION";
  const d = new Date(Date.now() + 2 * 864e5); // a sensible near-term ETA
  const p = (n) => String(n).padStart(2, "0");
  const eta = `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}`;
  return String(tpl || "").replace(/{destHub}/g, hub).replace(/{eta}/g, eta);
};

/** "25/07/2026"/"2026-07-25" -> a friendly day heading. */
const dayLabel = (raw) => {
  if (!raw) return "";
  let d = null;
  const s = String(raw).trim();
  const dmy = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (dmy) d = new Date(`${dmy[3]}-${dmy[2]}-${dmy[1]}`);
  else d = new Date(s);
  if (!d || Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" });
};
const timeLabel = (t) => {
  const s = String(t || "").trim();
  if (/^\d{3,4}$/.test(s)) return `${s.padStart(4, "0").slice(0, 2)}:${s.padStart(4, "0").slice(2)}`;
  return s;
};

/**
 * Vertical timeline: events grouped under a day heading, each on a card with a
 * dot on the rail — the layout couriers use, so staff can scan it quickly.
 */
function Timeline({ groups, emptyText, accent }) {
  if (!groups.length) return <MDTypography variant="caption" color="text">{emptyText}</MDTypography>;
  return (
    <MDBox sx={{ position: "relative", pl: 2.5 }}>
      <MDBox sx={{ position: "absolute", left: 7, top: 6, bottom: 6, width: "2px", backgroundColor: "#e3e6ea" }} />
      {groups.map((g, gi) => {
        // Timelines read oldest -> newest, so the LAST group is the current one.
        const isLatest = gi === groups.length - 1;
        return (
        <MDBox key={gi} mb={1.5}>
          <MDBox sx={{ position: "relative" }}>
            <MDBox
              sx={{
                position: "absolute", left: -21, top: 4, width: 12, height: 12, borderRadius: "50%",
                backgroundColor: isLatest ? accent : "#8fbf6a",
                boxShadow: isLatest ? `0 0 0 3px ${accent}33` : "none",
              }}
            />
            <MDTypography variant="caption" fontWeight="bold" display="block" sx={{ mb: 0.5 }}>
              {g.day}
            </MDTypography>
          </MDBox>
          {g.items.map((e, i) => (
            <MDBox
              key={i}
              sx={{ border: "1px solid #e8ebef", borderRadius: 2, p: 1, mb: 0.75, backgroundColor: "#fff" }}
            >
              <MDTypography variant="caption" color="text" display="block">
                {e.time}{e.location ? ` | ${e.location}` : ""}
              </MDTypography>
              <MDTypography variant="caption" fontWeight="medium">{e.title}</MDTypography>
            </MDBox>
          ))}
        </MDBox>
        );
      })}
    </MDBox>
  );
}

/** Group a flat list (already newest-first) into day buckets. */
const groupByDay = (items) => {
  const out = [];
  items.forEach((it) => {
    const day = it.day || "";
    const last = out[out.length - 1];
    if (last && last.day === day) last.items.push(it);
    else out.push({ day, items: [it] });
  });
  return out;
};

export default function RowDetail({ parcelId, onPublished, suggestion, onApprove, onReject, onSuggest, busy }) {
  const { showNotification } = useNotification();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const [phrases, setPhrases] = useState([]);
  const [form, setForm] = useState({ status: "Intransit", message: "", box: "", apply_all_boxes: false });
  const [saving, setSaving] = useState(false);
  const [weights, setWeights] = useState({});
  const [meta, setMeta] = useState({ transit: "", notes: "" });
  const [timelineTab, setTimelineTab] = useState(0);
  const [sug, setSug] = useState({ status: "", message: "" });
  // Warns before updating a shipment whose vendor details are incomplete.
  const [warn, setWarn] = useState({ open: false, missing: "", action: null });

  const load = () => {
    setLoading(true);
    getInternalDetail(parcelId)
      .then((r) => {
        setD(r.data);
        const w = {};
        (r.data.boxes || []).forEach((b) => { w[b._id] = b.weight; });
        setWeights(w);
        // Start the form on the shipment's CURRENT status so staff can see
        // where it is and change it directly.
        const cur = STATUS_BY_CODE[r.data.status];
        // Multi-box shipments almost always move together, so default to
        // updating every box; staff can switch to a single box if they diverge.
        const multiBox = (r.data.boxes || []).length > 1;
        setForm((f) => ({
          ...f,
          ...(cur ? { status: cur } : {}),
          apply_all_boxes: multiBox ? true : f.apply_all_boxes,
        }));
        setMeta({ transit: r.data.transit || "", notes: r.data.notes || "" });
      })
      .catch(() => showNotification("error", "Error", "Failed to load shipment detail"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    getTrackingStages()
      .then((r) => setPhrases((r.data || []).filter((p) => p.is_active !== false).sort((a, b) => (a.seq || 0) - (b.seq || 0))))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parcelId]);

  /**
   * Vendor + vendor AWB are what let us pull the courier's real tracking.
   * Without them an update is based only on the time-based journey, so ask the
   * user to confirm rather than silently letting it through.
   */
  const guardVendor = (action) => {
    const missingParts = [];
    if (d && !d.internal_vendor) missingParts.push("vendor");
    if (d && !d.vendor_awb) missingParts.push("vendor AWB");
    if (!missingParts.length) return action();
    setWarn({ open: true, missing: missingParts.join(" and "), action });
  };

  const publish = () => {
    // A status-only update is valid (that's what the legacy popup does) — the
    // message is optional, so don't block on it.
    if (!form.status) return showNotification("error", "Error", "Select a status");
    setSaving(true);
    manualInternalUpdate({ id: parcelId, ...form })
      .then((r) => {
        showNotification("success", "Published", r.data.notified ? "Update published — customer notified" : "Update published");
        setForm((f) => ({ ...f, message: "" }));
        load();
        onPublished && onPublished();
      })
      .catch((err) => showNotification("error", "Error", err?.response?.data?.message || "Publish failed"))
      .finally(() => setSaving(false));
  };

  const saveMeta = (patch) => {
    const next = { ...meta, ...patch };
    setMeta(next);
    setInternalFields({ id: parcelId, ...next })
      .then(() => showNotification("success", "Saved", "Shipment details updated"))
      .catch((err) => showNotification("error", "Error", err?.response?.data?.message || "Failed to save"));
  };

  const saveWeight = (boxId) => {
    setInternalBoxWeight({ box_id: boxId, weight: weights[boxId] })
      .then(() => showNotification("success", "Saved", "Box weight updated"))
      .catch(() => showNotification("error", "Error", "Failed to save weight"));
  };

  if (loading && !d) return <MDBox p={2}><MDTypography variant="caption">Loading…</MDTypography></MDBox>;
  if (!d) return null;

  // Split the published timeline per box when the shipment has more than one,
  // so staff can see exactly what each box's customer view looks like.
  const allTracks = d.our_timeline || [];
  const boxKey = (h) => String(h.box || (h.box_number != null ? `n${h.box_number}` : ""));
  const timelineTabs = [];
  if ((d.boxes || []).length > 1) {
    const packageTracks = allTracks.filter((h) => !boxKey(h));
    if (packageTracks.length) timelineTabs.push({ key: "pkg", label: "Package", items: packageTracks });
    d.boxes.forEach((b, i) => {
      const items = allTracks.filter((h) => boxKey(h) === String(b._id) || String(h.box_number) === String(b.number));
      timelineTabs.push({ key: b._id, label: `Box ${b.number || i + 1}`, items });
    });
  } else {
    timelineTabs.push({ key: "all", label: "All", items: allTracks });
  }
  const activeTimeline = (timelineTabs[Math.min(timelineTab, timelineTabs.length - 1)] || {}).items || [];

  const sugStatus = sug.status || (suggestion && suggestion.suggested_status) || "";
  const sugMessage = sug.message !== "" ? sug.message : (suggestion && suggestion.suggested_message) || "";

  return (
    <MDBox p={2} sx={{ backgroundColor: "#fafafa" }}>
      {/* suggested next update (moved here from the table for a cleaner grid) */}
      <Card sx={{ p: 2, mb: 2, border: suggestion ? "1px solid #1A73E8" : "1px solid #e8ebef" }}>
        <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <MDTypography variant="button" fontWeight="bold">Suggested next update</MDTypography>
          {suggestion ? (
            <>
              <Select
                size="small"
                value={sugStatus}
                sx={{ minWidth: 120, height: 34 }}
                onChange={(e) => setSug((x) => ({ ...x, status: e.target.value }))}
              >
                {STATUS_OPTIONS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
              </Select>
              <MDInput
                sx={{ flex: 1, minWidth: 260 }}
                value={sugMessage}
                onChange={(e) => setSug((x) => ({ ...x, message: e.target.value }))}
              />
              <MDButton
                size="small"
                color="success"
                disabled={busy}
                onClick={() => guardVendor(() => onApprove && onApprove({ status: sugStatus, message: sugMessage }))}
              >
                <Icon>check</Icon>&nbsp;Approve
              </MDButton>
              <MDButton size="small" color="error" disabled={busy} onClick={() => onReject && onReject()}>
                <Icon>close</Icon>&nbsp;Reject
              </MDButton>
            </>
          ) : (
            <>
              <MDTypography variant="caption" color="text">
                No suggestion pending for this shipment.
              </MDTypography>
              <MDButton size="small" variant="outlined" color="info" disabled={busy} onClick={() => guardVendor(() => onSuggest && onSuggest())}>
                {busy ? "…" : "Generate suggestion"}
              </MDButton>
            </>
          )}
        </MDBox>
      </Card>

      {/* details */}
      <MDBox mb={2} display="flex" gap={3} flexWrap="wrap">
        {[
          ["Branch", d.branch_code || d.branch],
          ["Country", d.country],
          ["Weight", d.weight],
          ["Service", d.service || "—"],
          ["Boxes", d.box_count],
          ["Vendor", d.internal_vendor || "—"],
          ["Vendor AWB", d.vendor_awb || "—"],
          ["Forwarding no", (d.forwarding && d.forwarding.number) || "—"],
          ["Forwarding vendor", (d.forwarding && d.forwarding.vendor_name) || "—"],
          ["Vendor service", (d.forwarding && d.forwarding.service) || "—"],
          [
            "Last notification",
            d.notify && d.notify.status
              ? `${d.notify.channel || ""} ${d.notify.status}${d.notify.at ? ` · ${new Date(d.notify.at).toLocaleString()}` : ""}`
              : "—",
          ],
        ].map(([k, v]) => (
          <MDBox key={k}>
            <MDTypography variant="caption" color="text" display="block">{k}</MDTypography>
            <MDTypography variant="button" fontWeight="medium">{v || "—"}</MDTypography>
          </MDBox>
        ))}
      </MDBox>

      <MDBox mb={2} display="flex" gap={2} alignItems="center" flexWrap="wrap">
        <MDBox display="flex" alignItems="center" gap={1}>
          <MDTypography variant="caption" color="text">Transit</MDTypography>
          <Select
            size="small"
            displayEmpty
            value={meta.transit || ""}
            sx={{ minWidth: 110, height: 34 }}
            onChange={(e) => saveMeta({ transit: e.target.value })}
          >
            <MenuItem value="">— not set —</MenuItem>
            <MenuItem value="AIR">AIR</MenuItem>
            <MenuItem value="ROAD">ROAD</MenuItem>
          </Select>
        </MDBox>
        <MDInput
          label="Staff notes"
          multiline
          sx={{ flex: 1, minWidth: 280 }}
          value={meta.notes || ""}
          onChange={(e) => setMeta((m) => ({ ...m, notes: e.target.value }))}
          onBlur={(e) => saveMeta({ notes: e.target.value })}
        />
      </MDBox>

      <Grid container spacing={2}>
        {/* vendor timeline */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2, height: "100%" }}>
            <MDTypography variant="button" fontWeight="bold">Vendor timeline</MDTypography>
            <Divider />
            {d.vendor_error ? (
              <MDTypography variant="caption" color="error">{d.vendor_error}</MDTypography>
            ) : (
              <Timeline
                accent="#1A73E8"
                emptyText="No vendor events (manual vendor, or no AWB yet)."
                groups={groupByDay(
                  (d.vendor_timeline || []).map((e) => ({
                    day: dayLabel(e.date), time: timeLabel(e.time), location: e.location, title: e.status,
                  }))
                )}
              />
            )}
          </Card>
        </Grid>

        {/* our timeline — split per box when the shipment has more than one */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2, height: "100%" }}>
            <MDTypography variant="button" fontWeight="bold">Customer timeline (published)</MDTypography>
            {timelineTabs.length > 1 && (
              <Tabs
                value={Math.min(timelineTab, timelineTabs.length - 1)}
                onChange={(_, v) => setTimelineTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ minHeight: 32, mb: 1 }}
              >
                {timelineTabs.map((t) => (
                  <Tab key={t.key} label={t.label} sx={{ minHeight: 32, py: 0.5 }} />
                ))}
              </Tabs>
            )}
            <Divider />
            <Timeline
              accent="#43a047"
              emptyText="No updates published yet."
              groups={groupByDay(
                (activeTimeline || []).map((h) => ({
                  day: h.date ? dayLabel(h.date) : "",
                  time: h.date ? new Date(h.date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "",
                  location: h.status,
                  title: h.message || h.status,
                }))
              )}
            />
          </Card>
        </Grid>

        {/* boxes */}
        {(d.boxes || []).length > 0 && (
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDTypography variant="button" fontWeight="bold">Boxes ({d.boxes.length})</MDTypography>
              <Divider />
              <MDBox display="flex" gap={2} flexWrap="wrap">
                {d.boxes.map((b) => (
                  <MDBox key={b._id} display="flex" alignItems="center" gap={1} sx={{ border: "1px solid #eee", borderRadius: 2, p: 1 }}>
                    <MDTypography variant="caption" fontWeight="medium">#{b.number}</MDTypography>
                    <MDInput
                      type="number"
                      label="Weight"
                      value={weights[b._id] ?? ""}
                      sx={{ width: 90 }}
                      onChange={(e) => setWeights((w) => ({ ...w, [b._id]: e.target.value }))}
                      onBlur={() => saveWeight(b._id)}
                    />
                    <MDTypography variant="caption" color="text">
                      {b.length}×{b.breadth}×{b.height}
                    </MDTypography>
                  </MDBox>
                ))}
              </MDBox>
            </Card>
          </Grid>
        )}

        {/* manual update */}
        <Grid item xs={12}>
          <Card sx={{ p: 2 }}>
            <MDTypography variant="button" fontWeight="bold">Add update / change status</MDTypography>
            <MDTypography variant="caption" color="text" display="block">
              Publishes to the customer timeline. Message is optional — you can change the status on its own.
              Customers are notified only when the status changes.
            </MDTypography>
            <Divider />
            <MDBox display="flex" gap={1} alignItems="center" flexWrap="wrap">
              <Select
                size="small"
                value={form.status}
                sx={{ minWidth: 130, height: 36 }}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
              >
                {STATUS_OPTIONS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
              </Select>
              <Select
                size="small"
                displayEmpty
                value=""
                sx={{ minWidth: 160, height: 36 }}
                onChange={(e) => {
                  const ph = phrases.find((p) => p._id === e.target.value);
                  if (ph) {
                    setForm((f) => ({ ...f, message: renderPhrase(ph.message, d), status: ph.status || f.status }));
                  }
                }}
              >
                <MenuItem value="">Use a saved phrase…</MenuItem>
                {phrases.map((p) => (
                  <MenuItem key={p._id} value={p._id}>{renderPhrase(p.message, d)}</MenuItem>
                ))}
              </Select>
              <MDInput
                label="Message"
                sx={{ flex: 1, minWidth: 260 }}
                value={form.message}
                onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
              />
              {(d.boxes || []).length > 1 && (
                <>
                  <Select
                    size="small"
                    displayEmpty
                    value={form.box}
                    disabled={form.apply_all_boxes}
                    sx={{ minWidth: 120, height: 36 }}
                    onChange={(e) => setForm((f) => ({ ...f, box: e.target.value }))}
                  >
                    <MenuItem value="">Whole package</MenuItem>
                    {d.boxes.map((b) => <MenuItem key={b._id} value={b._id}>Box #{b.number}</MenuItem>)}
                  </Select>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.apply_all_boxes}
                        onChange={(e) => setForm((f) => ({ ...f, apply_all_boxes: e.target.checked, box: "" }))}
                      />
                    }
                    label={<MDTypography variant="caption">All boxes</MDTypography>}
                  />
                </>
              )}
              <MDButton variant="gradient" color="success" disabled={saving} onClick={() => guardVendor(publish)}>
                <Icon>send</Icon>&nbsp;{saving ? "Publishing…" : "Publish"}
              </MDButton>
            </MDBox>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={warn.open} onClose={() => setWarn({ open: false, missing: "", action: null })} maxWidth="xs" fullWidth>
        <DialogTitle>Vendor details missing</DialogTitle>
        <DialogContent dividers>
          <MDTypography variant="button" fontWeight="medium" display="block">
            This shipment has no {warn.missing}.
          </MDTypography>
          <MDTypography variant="caption" color="text" display="block" sx={{ mt: 1 }}>
            Without it we can&apos;t fetch the courier&apos;s real tracking, so updates rely only on the
            time-based journey and the vendor timeline stays empty. Add the {warn.missing} on the
            shipment row for accurate tracking.
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton variant="text" color="secondary" onClick={() => setWarn({ open: false, missing: "", action: null })}>
            Cancel
          </MDButton>
          <MDButton
            variant="gradient"
            color="warning"
            onClick={() => {
              const run = warn.action;
              setWarn({ open: false, missing: "", action: null });
              if (run) run();
            }}
          >
            Continue anyway
          </MDButton>
        </DialogActions>
      </Dialog>
    </MDBox>
  );
}
